import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Target, Heart, Users, ShieldCheck, Globe, Leaf, Lock, Scale } from 'lucide-react';
import logoLiderIA from '@/assets/logo_lider_ia_white.png';
import logoVivetori from '@/assets/logo_vivetori_white.png';
import { SEO } from '@/components/SEO';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-16">
      <SEO
        title="Sobre LÍDER IA — Nuestra misión y equipo"
        description="Conoce a LÍDER IA: misión, valores y equipo que impulsa la adopción responsable de IA en Latinoamérica."
        path="/acerca"
      />
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl gradient-dark p-10 lg:p-16 text-center">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-secondary/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-accent/20 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col items-center gap-6">
          <img src={logoLiderIA} alt="LÍDER IA" className="h-12 w-auto" />
          <div className="flex items-center gap-3">
            <span className="text-white/70 text-sm italic">by</span>
            <img src={logoVivetori} alt="Vivetori" className="h-7 w-auto" />
          </div>
          <p className="text-lg text-white/80 max-w-3xl">
            Generamos impacto real con Inteligencia Artificial. No formamos solo líderes:
            empoderamos a cualquier profesional —de cualquier área, nivel o industria— para que
            pueda liderar la IA con propósito, ética y responsabilidad.
          </p>
        </div>
      </section>

      {/* Propósito */}
      <section className="grid lg:grid-cols-2 gap-8 items-center">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
            <Target className="w-4 h-4" /> Nuestro Propósito
          </div>
          <h2 className="text-3xl font-bold text-foreground">
            Democratizar el impacto de la IA en Latinoamérica
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            La Inteligencia Artificial no es exclusiva de perfiles técnicos ni de cargos directivos.
            Cualquier persona puede liderar la IA si cuenta con las herramientas, el criterio y el
            marco ético adecuado para hacerlo.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            Por eso nos enfocamos en <strong>impacto medible</strong>: proyectos reales que
            transforman organizaciones y comunidades, no solo aprendizaje teórico. Acompañamos a
            cada participante hasta convertir la IA en resultados tangibles.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { icon: Sparkles, title: 'IA con Propósito', desc: 'Tecnología al servicio de las personas.' },
            { icon: Users, title: 'Acceso Universal', desc: 'Para cualquier profesional, no solo líderes.' },
            { icon: ShieldCheck, title: 'Gobernanza Responsable', desc: 'Ética, seguridad y derechos humanos al centro.' },
            { icon: Globe, title: 'Impacto Medible', desc: 'Resultados tangibles en + 17 paises.' },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="p-5 rounded-2xl bg-card border border-border hover:border-primary/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">{title}</h3>
              <p className="text-sm text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Gobernanza e IA Responsable */}
      <section className="rounded-3xl bg-gradient-to-br from-primary/5 via-card to-secondary/5 border border-border p-10 lg:p-12">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium">
              <Scale className="w-4 h-4" /> Gobernanza e IA Responsable
            </div>
            <h2 className="text-3xl font-bold text-foreground">
              IA con conciencia, no solo con potencia
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Toda iniciativa que acompañamos contempla un marco integral de gobernanza para que la
              Inteligencia Artificial genere valor sin comprometer a las personas ni al planeta.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
            {[
              { icon: ShieldCheck, title: 'Ética y Transparencia', desc: 'Decisiones algorítmicas explicables y auditables.' },
              { icon: Lock, title: 'Seguridad y Privacidad', desc: 'Protección de datos y manejo responsable de la información.' },
              { icon: Heart, title: 'Derechos Humanos', desc: 'Respeto a la dignidad y los derechos fundamentales.' },
              { icon: Leaf, title: 'Impacto Ambiental', desc: 'Sostenibilidad y huella responsable de cada solución.' },
              { icon: Users, title: 'Inclusión y Equidad', desc: 'Sin sesgos ni discriminación; IA para todas y todos.' },
              { icon: Scale, title: 'Cumplimiento', desc: 'Alineación con marcos regulatorios y estándares globales.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-4 rounded-xl bg-card/60 backdrop-blur border border-border/60">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-primary" />
                  <h4 className="font-semibold text-foreground text-sm">{title}</h4>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vivetori */}
      <section className="rounded-3xl gradient-dark p-10 lg:p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-20 w-72 h-72 bg-secondary/15 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-20 w-72 h-72 bg-accent/15 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto space-y-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/90 text-sm font-medium mx-auto">
            <Heart className="w-4 h-4" /> Quiénes somos
          </div>
          <div className="flex items-center justify-center gap-3">
            <img src={logoVivetori} alt="Vivetori" className="h-10 w-auto" />
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold">
            Vivetori — <span className="text-secondary">AI Venture &amp; Innovation Builder</span>
          </h2>
          <p className="text-lg text-white/85 italic max-w-3xl mx-auto">
            Aceleramos la transformación y el crecimiento de tu compañía mediante soluciones basadas
            en inteligencia artificial.
          </p>
          <div className="space-y-4 text-white/80 leading-relaxed max-w-3xl mx-auto">
            <p>
              Desarrollamos soluciones apoyadas con inteligencia artificial y tecnologías 4.0 para
              mejorar la calidad de vida de las personas.
            </p>
            <p>
              Somos una fábrica de experiencias digitales disruptivas, al servicio del bienestar,
              sostenibilidad, accesibilidad y transformación global.
            </p>
          </div>
          <div className="pt-4">
            <p className="text-2xl lg:text-3xl font-bold text-secondary">
              Construyendo un mundo mejor.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="rounded-3xl gradient-dark p-10 lg:p-12 text-center text-white">
        <h2 className="text-3xl font-bold mb-3">¿Quieres ser parte del movimiento?</h2>
        <p className="text-white/80 mb-6 max-w-2xl mx-auto">
          Explora nuestros planes y comienza tu camino para liderar con IA.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button size="lg" onClick={() => navigate('/planes')}>Ver Planes</Button>
          <Button
            size="lg"
            variant="outline"
            className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-primary"
            onClick={() => navigate('/impacto')}
          >
            Conocer Nuestro Impacto
          </Button>
        </div>
      </section>
    </div>
  );
}
